/**
 * Created by Workaxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev.e@workaxis.in
 * Date: 21/10/2025
 * Time: 10:30 AM
 * Project: W-CRM
 */
import { createSlice } from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import { formatAxiosErrorMessage, qargsToQstring } from "../../utils/common";
import { endPoints } from "../../api/endpoints";

const ApiManagementInitialState = {
    credentials: {
        isLoading: false,
        data: [],
        error: null,
        count: 0,
        next: null,
        previous: null,
    },
    createCredential: {
        isLoading: false,
        data: null,
        error: null,
    },
    updateCredential: {
        isLoading: false,
        data: null,
        error: null,
    },
    deleteCredential: {
        isLoading: false,
        data: null,
        error: null,
    },
    revokeCredential: {
        isLoading: false,
        data: null,
        error: null,
    },
};

export const ApiManagementSlice = createSlice({
    name: "apiManagement",
    initialState: {
        ...ApiManagementInitialState,
    },
    reducers: {
        // Fetch Credentials
        FetchCredentialsRequesting: (state) => {
            state.credentials.isLoading = true;
            state.credentials.error = null;
        },
        FetchCredentialsSuccess: (state, action) => {
            state.credentials.isLoading = false;
            state.credentials.error = null;
            // Backend returns { count, next, previous, results: [...] }
            state.credentials.data = action.payload.results || action.payload.data || [];
            state.credentials.count = action.payload.count || 0;
            state.credentials.next = action.payload.next || null;
            state.credentials.previous = action.payload.previous || null;
        },
        FetchCredentialsError: (state, action) => {
            state.credentials.isLoading = false;
            state.credentials.data = [];
            state.credentials.error = action.payload;
        },
        FetchCredentialsReset: (state) => {
            state.credentials = ApiManagementInitialState.credentials;
        },

        // Create Credential
        CreateCredentialRequesting: (state) => {
            state.createCredential.isLoading = true;
            state.createCredential.error = null;
        },
        CreateCredentialSuccess: (state, action) => {
            state.createCredential.isLoading = false;
            state.createCredential.error = null;
            state.createCredential.data = action.payload;
            // Don't auto-add to list - let the component refetch to maintain proper filtering
        },
        CreateCredentialError: (state, action) => {
            state.createCredential.isLoading = false;
            state.createCredential.data = null;
            state.createCredential.error = action.payload;
        },
        CreateCredentialReset: (state) => {
            state.createCredential = ApiManagementInitialState.createCredential;
        },

        // Update Credential
        UpdateCredentialRequesting: (state) => {
            state.updateCredential.isLoading = true;
            state.updateCredential.error = null;
        },
        UpdateCredentialSuccess: (state, action) => {
            state.updateCredential.isLoading = false;
            state.updateCredential.error = null;
            state.updateCredential.data = action.payload;
            // Update in credentials list
            const index = state.credentials.data.findIndex(
                (cred) => cred.id === action.payload.id
            );
            if (index !== -1) {
                state.credentials.data[index] = action.payload;
            }
        },
        UpdateCredentialError: (state, action) => {
            state.updateCredential.isLoading = false;
            state.updateCredential.data = null;
            state.updateCredential.error = action.payload;
        },
        UpdateCredentialReset: (state) => {
            state.updateCredential = ApiManagementInitialState.updateCredential;
        },

        // Delete Credential
        DeleteCredentialRequesting: (state) => {
            state.deleteCredential.isLoading = true;
            state.deleteCredential.error = null;
        },
        DeleteCredentialSuccess: (state, action) => {
            state.deleteCredential.isLoading = false;
            state.deleteCredential.error = null;
            state.deleteCredential.data = action.payload;
            // Remove from credentials list by ID passed in payload
            const deletedId = action.payload.id;
            if (deletedId) {
                state.credentials.data = state.credentials.data.filter(
                    (cred) => cred.id !== deletedId
                );
            }
        },
        DeleteCredentialError: (state, action) => {
            state.deleteCredential.isLoading = false;
            state.deleteCredential.data = null;
            state.deleteCredential.error = action.payload;
        },
        DeleteCredentialReset: (state) => {
            state.deleteCredential = ApiManagementInitialState.deleteCredential;
        },

        // Revoke Credential
        RevokeCredentialRequesting: (state) => {
            state.revokeCredential.isLoading = true;
            state.revokeCredential.error = null;
        },
        RevokeCredentialSuccess: (state, action) => {
            state.revokeCredential.isLoading = false;
            state.revokeCredential.error = null;
            state.revokeCredential.data = action.payload;
            // Update status in credentials list
            const index = state.credentials.data.findIndex(
                (cred) => cred.id === action.payload.id
            );
            if (index !== -1) {
                state.credentials.data[index] = action.payload;
            }
        },
        RevokeCredentialError: (state, action) => {
            state.revokeCredential.isLoading = false;
            state.revokeCredential.data = null;
            state.revokeCredential.error = action.payload;
        },
        RevokeCredentialReset: (state) => {
            state.revokeCredential = ApiManagementInitialState.revokeCredential;
        },


        ApiManagementMasterReset: () => ApiManagementInitialState,
    },
});

// Selectors
export const ApiManagementState = (state) => state.apiManagement;

// Thunks/API Calls
export const fetchCredentialsAPI = (query = {}) => async (dispatch) => {
    dispatch(FetchCredentialsRequesting());
    try {
        const stringQuery = qargsToQstring(query);
        const response = await apiService.get(
            `${endPoints.apiManagement.list}${stringQuery}`
        );
        // Backend response: { error: false, data: { count, results: [...] } }
        dispatch(FetchCredentialsSuccess(response.data.data || response.data));
    } catch (error) {
        dispatch(FetchCredentialsError(formatAxiosErrorMessage(error)));
    }
};

export const createCredentialAPI = (data) => async (dispatch) => {
    dispatch(CreateCredentialRequesting());
    try {
        const payload = {
            name: data.name,
            // Coerce "no-expire" (if passed) and empty values to null
            expires_in_days:
                data.expires_in_days === "no-expire" || data.expires_in_days == null || data.expires_in_days === ""
                    ? null
                    : data.expires_in_days,
            allowed_ips: data.allowed_ips == null || data.allowed_ips === "" ? null : data.allowed_ips,
        };
        const response = await apiService.post(
            endPoints.apiManagement.create,
            payload
        );
        dispatch(CreateCredentialSuccess(response.data.data || response.data));
    } catch (error) {
        dispatch(CreateCredentialError(formatAxiosErrorMessage(error)));
    }
};

export const updateCredentialAPI = (id, data) => async (dispatch) => {
    dispatch(UpdateCredentialRequesting());
    try {
        const url = endPoints.apiManagement.update.replace("{{id}}", id);
        const payload = {};
        if (data.name !== undefined) {
            payload.name = data.name;
        }
        if (data.expires_in_days !== undefined) {
            payload.expires_in_days =
                data.expires_in_days === "no-expire" || data.expires_in_days == null || data.expires_in_days === ""
                    ? null
                    : data.expires_in_days;
        }
        if (data.allowed_ips !== undefined) {
            payload.allowed_ips = data.allowed_ips == null || data.allowed_ips === "" ? null : data.allowed_ips;
        }
        const response = await apiService.patch(url, payload);
        dispatch(UpdateCredentialSuccess(response.data.data || response.data));
    } catch (error) {
        dispatch(UpdateCredentialError(formatAxiosErrorMessage(error)));
    }
};

export const deleteCredentialAPI = (id) => async (dispatch) => {
    dispatch(DeleteCredentialRequesting());
    try {
        const url = endPoints.apiManagement.delete.replace("{{id}}", id);
        await apiService.delete(url);
        // Backend returns 204 No Content, so we handle it manually
        dispatch(DeleteCredentialSuccess({ id }));
    } catch (error) {
        dispatch(DeleteCredentialError(formatAxiosErrorMessage(error)));
    }
};

export const revokeCredentialAPI = (id, reason = "") => async (dispatch) => {
    dispatch(RevokeCredentialRequesting());
    try {
        const url = endPoints.apiManagement.revoke.replace("{{id}}", id);
        const response = await apiService.post(url, { revoke_reason: reason });
        dispatch(RevokeCredentialSuccess(response.data.data || response.data));
    } catch (error) {
        dispatch(RevokeCredentialError(formatAxiosErrorMessage(error)));
    }
};

export const {
    FetchCredentialsRequesting,
    FetchCredentialsSuccess,
    FetchCredentialsError,
    FetchCredentialsReset,
    CreateCredentialRequesting,
    CreateCredentialSuccess,
    CreateCredentialError,
    CreateCredentialReset,
    UpdateCredentialRequesting,
    UpdateCredentialSuccess,
    UpdateCredentialError,
    UpdateCredentialReset,
    DeleteCredentialRequesting,
    DeleteCredentialSuccess,
    DeleteCredentialError,
    DeleteCredentialReset,
    RevokeCredentialRequesting,
    RevokeCredentialSuccess,
    RevokeCredentialError,
    RevokeCredentialReset,
    ApiManagementMasterReset,
} = ApiManagementSlice.actions;

export default ApiManagementSlice.reducer;
